import $http from "../../axios-instance";
import $httpWor from "../../axios-instance-wor";
import Swal from "sweetalert2";
import moment from "moment";

/**
 * Obtiene los tipos de reportes por el id de la compañía con opciones de paginación y búsqueda.
 *
 * @param {*} commit
 * @param {object} options - Opciones para la consulta
 * @param {number} options.companyId - ID de la compañía
 * @param {string} options.searchQuery - Término de búsqueda
 * @param {number} options.page - Página actual
 * @param {number} options.limit - Límite de elementos por página
 * @param {string} options.sortBy - Campo por el cual ordenar
 * @param {string} options.sortOrder - Orden de los resultados (ASC o DESC)
 * @returns {Promise}
 */
export const getReportTypes = async (
    { commit },
    {
        companyId,
        searchQuery = "",
        page = 1,
        limit = 10,
        sortBy = "id",
        sortOrder = "DESC",
    }
) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/report-type/list/?company_id=${companyId}&search=${searchQuery}&page=${page}&limit=${limit}&sortBy=${sortBy}&sort=${sortOrder}`
        );
        commit("setReportTypes", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener los tipos de reportes",
            text: error.message,
        });
    }
};

/**
 * Obtiene los niveles de riesgo por el id de la compañía con opciones de paginación y búsqueda.
 *
 * @param {*} commit
 * @param {object} options - Opciones para la consulta
 * @param {number} options.companyId - ID de la compañía
 * @param {string} options.searchQuery - Término de búsqueda
 * @param {number} options.page - Página actual
 * @param {number} options.limit - Límite de elementos por página
 * @param {string} options.sortBy - Campo por el cual ordenar
 * @param {string} options.sortOrder - Orden de los resultados (ASC o DESC)
 * @returns {Promise}
 */
export const getLevelsOfRisks = async (
    { commit },
    {
        companyId,
        searchQuery = "",
        page = 1,
        limit = 10,
        sortBy = "id",
        sortOrder = "DESC",
    }
) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/level-of-risk/list/?company_id=${companyId}&search=${searchQuery}&page=${page}&limit=${limit}&sortBy=${sortBy}&sort=${sortOrder}`
        );
        commit("setLevelsOfRisks", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener los niveles de riesgo",
            text: error.message,
        });
    }
};

/**
 * Obtiene las acciones correctivas por el id de la compañía con opciones de paginación y búsqueda.
 *
 * @param {*} commit
 * @param {object} options - Opciones para la consulta
 * @param {number} options.companyId - ID de la compañía
 * @param {string} options.searchQuery - Término de búsqueda
 * @param {number} options.page - Página actual
 * @param {number} options.limit - Límite de elementos por página
 * @param {string} options.sortBy - Campo por el cual ordenar
 * @param {string} options.sortOrder - Orden de los resultados (ASC o DESC)
 * @returns {Promise}
 */
export const getCorrectiveActions = async (
    { commit },
    {
        companyId,
        searchQuery = "",
        page = 1,
        limit = 10,
        sortBy = "id",
        sortOrder = "DESC",
    }
) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/corrective-action/list/?company_id=${companyId}&search=${searchQuery}&page=${page}&limit=${limit}&sortBy=${sortBy}&sort=${sortOrder}`
        );
        commit("setCorrectiveActions", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener las acciones correctivas",
            text: error.message,
        });
    }
};

/**
 * Crear un nuevo tipo de reporte.
 *
 * @param {*} commit
 * @param {object} reportType - Datos del tipo de reporte
 *
 * @returns {Promise}
 */
export const createReportType = async ({ commit }, reportType) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.post(
            "/admin-reports/report-type/create",
            reportType
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Tipo de estudio creado",
        });

        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        if (this.$vue.$alertErrorsCallback(error)) return false;
        this.$vue.$alertError(error.message);

        return false;
    }
};

/**
 * Crear un nuevo nivel de riesgo.
 *
 * @param {*} commit
 * @param {object} levelOfRisk - Datos del nivel de riesgo
 *
 * @returns {Promise}
 */
export const createLevelOfRisk = async ({ commit }, levelOfRisk) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.post(
            "/admin-reports/level-of-risk/create",
            levelOfRisk
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Nivel de riesgo creado",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message,
        });
        return false;
    }
};

/**
 * Crear una nueva recomendación.
 *
 * @param {*} commit
 * @param {object} correctiveAction - Datos de la recomendación
 *
 * @returns {Promise}
 */
export const createCorrectiveAction = async ({ commit }, correctiveAction) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.post(
            "/admin-reports/corrective-action/create",
            correctiveAction
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Escenario creado",
        });

        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al crear la recomendación",
            text: error.message,
        });

        return false;
    }
};

/**
 * Obtenemos un tipo de reporte por su id.
 *
 * @param {*} commit
 * @param {number} id - ID del tipo de reporte
 *
 * @returns {Promise}
 */
export const getReportTypeById = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/report-type/show/${data.id}?company_id=${data.company_id}`
        );
        commit("dashboard/load", false, { root: true });
        return response.data.data;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener el tipo de reporte",
            text: error.message,
        });
    }
};

/**
 * Obtenemos un nivel de riesgo por su id.
 *
 * @param {*} commit
 * @param {number} id - ID del nivel de riesgo
 *
 * @returns {Promise}
 */
export const getLevelOfRiskById = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/level-of-risk/show/${data.id}?company_id=${data.company_id}`
        );
        commit("dashboard/load", false, { root: true });
        return response.data.data;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener el nivel de riesgo",
            text: error.message,
        });
    }
};

/**
 * Obtenemos una recomendación por su id.
 *
 * @param {*} commit
 * @param {number} id - ID de la recomendación
 *
 * @returns {Promise}
 */
export const getCorrectiveActionById = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/corrective-action/show/${data.id}?company_id=${data.company_id}`
        );
        commit("dashboard/load", false, { root: true });
        return response.data.data;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener la recomendación",
            text: error.message,
        });
    }
};

/**
 * Actualizar un tipo de reporte por su id.
 *
 * @param {*} commit
 * @param {object} reportType - Datos del tipo de reporte
 *
 * @returns {Promise}
 */
export const updateReportType = async ({ commit }, reportType) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(
            `/admin-reports/report-type/update/${reportType.id}`,
            reportType
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Tipo de reporte actualizado",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al actualizar el tipo de reporte",
            text: error.message,
        });
        return false;
    }
};

/**
 * Actualizar un nivel de riesgo por su id.
 *
 * @param {*} commit
 * @param {object} levelOfRisk - Datos del nivel de riesgo
 *
 * @returns {Promise}
 */
export const updateLevelOfRisk = async ({ commit }, levelOfRisk) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(
            `/admin-reports/level-of-risk/update/${levelOfRisk.id}`,
            levelOfRisk
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Nivel de riesgo actualizado",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al actualizar el nivel de riesgo",
            text: error.message,
        });
        return false;
    }
};

/**
 * Actualizar una recomendación por su id.
 *
 * @param {*} commit
 * @param {object} correctiveAction - Datos de la recomendación
 *
 * @returns {Promise}
 */
export const updateCorrectiveAction = async ({ commit }, correctiveAction) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(
            `/admin-reports/corrective-action/update/${correctiveAction.id}`,
            correctiveAction
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "recomendación actualizada",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al actualizar la recomendación",
            text: error.message,
        });

        return false;
    }
};

/**
 * Cambiar el estado de un tipo de reporte y usa dispatch para llamar a la acción updateReportType.
 *
 * @param {*} commit
 * @param {object} data - Datos del tipo de reporte
 *
 * @returns {Promise}
 */
export const changeReportTypeStatus = async ({ commit, dispatch }, data) => {
    await Swal.fire({
        title: data.is_active
            ? "¿Desactivar tipo de reporte?"
            : "¿Activar tipo de reporte?",
        text: data.is_active
            ? "El tipo de reporte se desactivará"
            : "El tipo de reporte se activará",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonText: "Si",
    }).then(async (result) => {
        if (result.value) {
            data.is_active = !data.is_active;
            await dispatch("updateReportType", data);
        }
    });
};

/**
 * Cambiar el estado de un nivel de riesgo y usa dispatch para llamar a la acción updateLevelOfRisk.
 *
 * @param {*} commit
 * @param {object} data - Datos del nivel de riesgo
 *
 * @returns {Promise}
 */
export const changeLevelOfRiskStatus = async ({ commit, dispatch }, data) => {
    await Swal.fire({
        title: data.is_active
            ? "¿Desactivar nivel de riesgo?"
            : "¿Activar nivel de riesgo?",
        text: data.is_active
            ? "El nivel de riesgo se desactivará"
            : "El nivel de riesgo se activará",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonText: "Si",
    }).then(async (result) => {
        if (result.value) {
            data.is_active = !data.is_active;
            await dispatch("updateLevelOfRisk", data);
        }
    });
};

/**
 * Cambiar el estado de una recomendación y usa dispatch para llamar a la acción updateCorrectiveAction.
 *
 * @param {*} commit
 * @param {object} data - Datos de la recomendación
 *
 * @returns {Promise}
 */

export const changeCorrectiveActionStatus = async (
    { commit, dispatch },
    data
) => {
    await Swal.fire({
        title: data.is_active
            ? "¿Desactivar recomendación?"
            : "¿Activar recomendación?",
        text: data.is_active
            ? "La recomendación se desactivará"
            : "La recomendación se activará",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonText: "Si",
    }).then(async (result) => {
        if (result.value) {
            data.is_active = !data.is_active;
            await dispatch("updateCorrectiveAction", data);
        }
    });
};

/**
 * Obtiene los tipos de reportes por el id de la compañía con opciones de paginación y búsqueda.
 *
 * @param {*} commit
 * @param {object} options - Opciones para la consulta
 * @param {number} options.companyId - ID de la compañía
 * @param {string} options.searchQuery - Término de búsqueda
 * @param {number} options.page - Página actual
 * @param {number} options.limit - Límite de elementos por página
 * @param {string} options.sortBy - Campo por el cual ordenar
 * @param {string} options.sortOrder - Orden de los resultados (ASC o DESC)
 * @param {string} options.state - Estado del reporte
 * @param {string} options.responsible_id - ID del responsable
 * @param {string} options.report_type_id - ID del tipo de reporte
 * @param {string} options.date_range - Rango de fechas
 * @returns {Promise}
 */
export const getReports = async (
    { commit },
    {
        companyId,
        searchQuery = "",
        page = 1,
        limit = 10,
        sortBy = "id",
        sortOrder = "DESC",
        state = [],
        responsible_id = "",
        report_type_id = "",
        date_range = [],
        location_id = "",
    }
) => {
    commit("dashboard/load", true, { root: true });

    // Crear un objeto params con los parámetros que siempre estarán presentes.
    let params = {
        company_id: companyId,
        search: searchQuery,
        page,
        limit,
        sortBy,
        sort: sortOrder,
    };

    // Agregar condicionalmente parámetros que puedan estar ausentes o sean opcionales.
    if (state.length > 0) params.state = state;
    if (responsible_id) params.responsible_id = responsible_id;
    if (report_type_id) params.report_type_id = report_type_id;
    if (location_id) params.location_id = location_id;
    if (date_range && date_range.length === 2) {
        //formatear las fechas a 'YYYY-MM-DD'
        params.start_date = date_range[0].toISOString().split("T")[0];
        params.end_date = date_range[1].toISOString().split("T")[0];
    }

    // Construir la cadena de consulta (query string) de manera dinámica.
    const queryString = Object.entries(params)
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return value
                    .map(
                        (v) =>
                            `${encodeURIComponent(key)}[]=${encodeURIComponent(
                                v
                            )}`
                    )
                    .join("&");
            } else {
                return `${encodeURIComponent(key)}=${encodeURIComponent(
                    value
                )}`;
            }
        })
        .join("&");

    try {
        const response = await $http.get(`/reports/list/?${queryString}`);
        commit("setReports", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener los tipos de reportes",
            text: error.message,
        });
    }
};

/**
 * Obtener ubicaiones por el id de la compañía.
 *
 * @param {*} commit
 * @param {number} companyId - ID de la compañía
 * @returns {Promise}
 */
export const getLocations = async ({ commit }, companyId) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(`/company/location/tree/${companyId}`);
        commit("setLocations", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener las ubicaciones",
            text: error.message,
        });
    }
};

/**
 * Obtiene los tipos de reportes por el id de la compañía activos.
 *
 * @param {*} commit
 * @param {number} companyId - ID de la compañía
 * @returns {Promise}
 */
export const getReportTypesActives = async ({ commit }, companyId) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/report-type/list-active/?company_id=${companyId}&is_active=true`
        );
        commit("setReportTypesActives", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener los tipos de reportes",
            text: error.message,
        });
    }
};

/**
 * Obtiene los niveles de riesgo por el id de la compañía activos.
 *
 * @param {*} commit
 * @param {number} companyId - ID de la compañía
 * @returns {Promise}
 */
export const getLevelsOfRisksActives = async ({ commit }, companyId) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/level-of-risk/list-active/?company_id=${companyId}&is_active=true`
        );
        commit("setLevelsOfRisksActives", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener los niveles de riesgo",
            text: error.message,
        });
    }
};

/**
 * Obtiene las acciones correctivas por el id de la compañía activas.
 *
 * @param {*} commit
 * @param {number} companyId - ID de la compañía
 * @returns {Promise}
 */
export const getCorrectiveActionsActives = async ({ commit }, companyId) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/corrective-action/list-active/?company_id=${companyId}&is_active=true`
        );
        commit("setCorrectiveActionsActives", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener las acciones correctivas",
            text: error.message,
        });
    }
};

/**
 * Obtiene los responsables por el id de la compañía.
 *
 * @param {*} commit
 * @param {number} companyId - ID de la compañía
 * @returns {Promise}
 */
export const getResponsibles = async ({ commit }, companyId) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $httpWor.get(
            `/worker/list-actives-by-company/${companyId}`
        );
        commit("setResponsibles", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener los responsables",
            text: error.message,
        });
    }
};

/**
 * Crear un nuevo reporte deve ser con formData ya que vamos a enviar archivos valida si viene el campo evidence.
 *
 * @param {*} commit
 * @param {object} report - Datos del reporte
 *
 * @returns {Promise}
 */
export const createReport = async ({ commit }, report) => {
    let formData = new FormData();
    if (report.evidence && Array.isArray(report.evidence)) {
        // Si evidence es un arreglo, iterar sobre cada archivo
        report.evidence.forEach((file) => {
            formData.append("evidence[]", file); // Añade cada archivo al FormData
        });
    } else {
        delete report.evidence;
    }
    Object.entries(report).forEach(([key, value]) => {
        if (key === "corrective_actions") {
            value.forEach((element, index) => {
                Object.entries(element).forEach(([key, value]) => {
                    formData.append(
                        `corrective_actions[${index}][${key}]`,
                        value
                    );
                });
            });
        } else {
            formData.append(key, value);
        }
    });
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.post("/reports/create", formData);
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Estudio creado",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        const htmlErrors = window.listErrors(error);
        Swal.fire({
            icon: "error",
            title: error.message,
            html: htmlErrors,
        });
        return false;
    }
};

/**
 * Obtenemos un reporte por su id.
 *
 * @param {*} commit
 * @param {} data - reporte
 *
 * @returns {Promise}
 */
export const getReportById = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/reports/show/${data.id}?company_id=${data.company_id}`
        );
        commit("dashboard/load", false, { root: true });
        return response.data.data;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener el reporte",
            text: error.message,
        });
    }
};

/**
 * Actualizar un reporte por su id.
 *
 * @param {*} commit
 * @param {object} report - Datos del reporte
 *
 * @returns {Promise}
 */
export const updateReport = async ({ commit }, report) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(
            `/reports/update/${report.id}`,
            report
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Reporte de hallazgo actualizado",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al actualizar el reporte",
            text: error.message,
        });
        return false;
    }
};

/**
 * Agregar una recomendación a un reporte.
 *
 * @param {*} commit
 * @param {object} correctiveAction - Datos de la recomendación
 *
 * @returns {boolean}
 */
export const addCorrectiveActionToReport = async (
    { commit },
    correctiveAction
) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.post(
            "/reports/add-corrective-action",
            correctiveAction
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Recomendación agregada al estudio",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al agregar la recomendación al reporte",
            text: error.message,
        });
        return false;
    }
};

/**
 * Obtener una recomendación por su id con todo su detalle.
 *
 * @param {*} commit
 * @param {object} {id} - ID de la recomendación, {company_id} - ID de la compañía
 *
 * @returns {Promise}
 */
export const getCorrectiveActionDetail = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/reports/get-corrective-action/${data.id}?company_id=${data.company_id}`
        );
        commit("dashboard/load", false, { root: true });
        return response.data.data;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener la recomendación",
            text: error.message,
        });
    }
};

/**
 * Agregar un histórico de una recomendación.
 *
 * @param {*} commit
 * @param {object} data - Datos de la recomendación
 *
 * @returns {Promise}
 */
export const addCorrectiveActionHistory = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        let formData = new FormData();
        if (data.evidences && Array.isArray(data.evidences)) {
            data.evidences.forEach((file) => {
                formData.append("evidences[]", file);
            });
        } else {
            delete data.evidences;
        }

        formData.append(
            "report_corrective_action_id",
            data.report_corrective_action_id
        );
        formData.append("company_id", data.company_id);
        formData.append("comment", data.comment);

        const response = await $http.post(
            "/reports/add-history-corrective-action",
            formData
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Nota agregada a la recomendación",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al agregar la nota a la recomendación",
            text: error.message,
        });
        return false;
    }
};

/**
 * Marcar como resuelta una recomendación.
 *
 * @param {*} commit
 * @param {object} data - Datos de la recomendación
 *
 * @returns {Promise}
 */
export const resolveCorrectiveAction = async ({ commit }, data) => {
    const result = await Swal.fire({
        title: "¿Marcar como completada la recomendación?",
        text: "La recomendación se marcará como completada",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonText: "Si",
    });

    if (result.value) {
        commit("dashboard/load", true, { root: true });
        try {
            const response = await $http.post(
                `/reports/mark-as-completed-action-corrective/${data.id}`,
                data
            );
            commit("dashboard/load", false, { root: true });
            Swal.fire({
                icon: "success",
                title: response.data.message,
                text: "recomendación marcada como completada",
            });
            return true;
        } catch (error) {
            commit("dashboard/load", false, { root: true });
            Swal.fire({
                icon: "error",
                title: "Error al resolver la recomendación",
                text: error.message,
            });
            return false;
        }
    } else {
        return false;
    }
};

/**
 * Marcar como  resuelto un reporte.
 *
 * @param {*} commit
 * @param {object} data - Datos del reporte
 *
 * @returns {Promise}
 */
export const resolveReport = async ({ commit }, data) => {
    const result = await Swal.fire({
        title: "¿Marcar como completado el reporte?",
        text: "El reporte se marcará como completado",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonText: "Si",
    });

    if (result.value) {
        commit("dashboard/load", true, { root: true });
        try {
            const response = await $http.post(
                `/reports/mark-as-completed-report/${data.id}`,
                data
            );
            commit("dashboard/load", false, { root: true });
            Swal.fire({
                icon: "success",
                title: response.data.message,
                text: "Estudio marcado como completado",
            });
            return true;
        } catch (error) {
            commit("dashboard/load", false, { root: true });
            Swal.fire({
                icon: "error",
                title: "Error al resolver el reporte",
                text: error.message,
            });
            return false;
        }
    } else {
        return false;
    }
};

/**
 * Cancelar un reporte.
 *
 * @param {*} commit
 * @param {object} data - Datos del reporte
 *
 * @returns {Promise}
 */
export const cancelReport = async ({ commit }, data) => {
    const result = await Swal.fire({
        title: "¿Cancelar el estudio?",
        text: "El estudio se cancelará",
        icon: "question",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        cancelButtonText: "Cancelar",
        confirmButtonText: "Si",
    });

    if (result.value) {
        commit("dashboard/load", true, { root: true });
        try {
            const response = await $http.post(
                `/reports/cancel-report/${data.id}`,
                data
            );
            commit("dashboard/load", false, { root: true });
            Swal.fire({
                icon: "success",
                title: response.data.message,
                text: "Reporte cancelado",
            });
            return true;
        } catch (error) {
            commit("dashboard/load", false, { root: true });
            Swal.fire({
                icon: "error",
                title: "Error al cancelar el reporte",
                text: error.message,
            });
            return false;
        }
    } else {
        return false;
    }
};

/**
 * Descargar un archivo zip de las evidencias de un reporte por su id en base64.
 *
 * @param {*} commit
 * @param {object} data - Datos del reporte
 *
 * @returns {Promise}
 */
export const downloadReportEvidences = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/reports/dowload-all-evidences/${data.id}?company_id=${data.company_id}`
        );
        //Genera archivo apartir de base64 descargar.
        const link = document.createElement("a");
        link.href = `data:application/zip;base64,${response.data.data}`;
        link.download = "EVIDENCIAS-HALLAZGO-" + data.code + ".zip";
        link.click();

        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al descargar las evidencias",
            text: error.message,
        });
    }
};

/**
 * Cambiar el nivel de riesgo de una recomendación.
 *
 * @param {*} commit
 * @param {object} data - Datos del reporte
 *
 * @returns {Promise}
 */
export const changeReportLevelOfRisk = async ({ commit }, data) => {
    const id = data.id;
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.put(
            `/reports/change-level-of-risk/${id}`,
            data
        );
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "success",
            title: response.data.message,
            text: "Nivel de riesgo de la recomendación actualizado.",
        });
        return true;
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al cambiar el nivel de riesgo de la recomendación",
            text: error.message,
        });
        return false;
    }
};

/**
 * Obtiene los niveles de riesgo por el id activos menores al nivel de riesgo actual.
 *
 * @param {*} commit
 * @param {object} data - Datos del reporte
 *
 * @returns {Promise}
 */
export const getLevelsOfRisksActivesLess = async ({ commit }, data) => {
    commit("dashboard/load", true, { root: true });
    try {
        const response = await $http.get(
            `/admin-reports/level-of-risk/list-active-minor-current-level/${data.level}?company_id=${data.company_id}`
        );
        commit("setLevelsOfRisksActivesLess", response.data.data);
        commit("dashboard/load", false, { root: true });
    } catch (error) {
        commit("dashboard/load", false, { root: true });
        Swal.fire({
            icon: "error",
            title: "Error al obtener los niveles de riesgo",
            text: error.message,
        });
    }
};

/**
 * Actualizar trabajador de una recomendación.
 *
 * @param {*} payload
 */
export async function updateRepReportCorrectiveAction({ commit }, payload) {
    try {
        commit("dashboard/load", true, { root: true });
        const response = await $http.patch(
            `/reports/update-rep-report-corrective-action`,
            payload
        );
        this.$vue.$alertSuccess("Recomendación actualizada");
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Obtener acciones correctivas de un hallazgo.
 *
 * @param {*} payload
 */
export async function getCorrectionActionsByFinding({ commit }, payload) {
    try {
        commit("dashboard/load", true, { root: true });
        const response = await $http.get(
            `/reports/get-corrective-action-by-findings`,
            { params: payload }
        );
        commit("setCorrectiveActionsList", response.data.data);
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Cambiar el estado de inactivo de un hallazgo.
 *
 * @param {*} id, company_id
 */
export async function deleteFindind({ commit }, { id, company_id }) {
    try {
        commit("dashboard/load", true, { root: true });
        const response = await $http.patch(
            `/reports/delete/${id}/${company_id}`
        );
        this.$vue.$alertSuccess(response.data.message);
        return response;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Descargar plantilla para importar información de acciones correctivas.
 * @param commit
 * @param data : {report_id, company_id}
 */
export async function downloadFormatActCorrect({ commit }, data) {
    commit("dashboard/load", true, { root: true });
    await $http({
        url: `reports/corrective-actions-download-format`,
        method: "POST",
        responseType: "blob",
        data,
    })
        .then((response) => {
            commit("dashboard/load", false, { root: true });
            const dateTime = moment().format("YYMMDDHHmm");
            window.downloadFile(
                response.data,
                `formato_importacion_${dateTime}`,
                ".xlsx"
            );
        })
        .catch(async (error) => {
            if (this.$vue.$alertErrorsCallback(error)) return;
            this.$vue.$alertError(error.message);
        })
        .finally(async () => {
            commit("dashboard/load", false, { root: true });
        });
}

/*
 * Cargar información en Excel para importación de acciones correctivas de un hallazgo.
 *
 * @param commit
 * @param payload
 * @returns {Promise<Object>}
 */
export async function uploadFileCorrectInfo({ commit }, payload) {
    try {
        commit("dashboard/load", true, { root: true });
        const response = await $http.post(
            `reports/corrective-actions-import-info`,
            payload
        );
        return response.data;
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return;
        this.$vue.$alertError(error.message, 5000);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}

/**
 * Exportar recomendaciones de una empresa.
 *
 * @param {*} commit
 * @param {object} filter - Filtros de búsqueda
 * @param {string} searchQuery - Término de búsqueda
 * @returns {Promise} void
 */
export async function exportRecommendations({ commit }, { filter, searchQuery }) {
    try {
        let formFilters = {};

        if (filter.company.id) formFilters.company_id = filter.company.id;
        if (filter.location_id) formFilters.location_id = filter.location_id;
        if (filter.report_type_id) formFilters.report_type_id = filter.report_type_id;
        if (filter.responsible_id) formFilters.responsible_id = filter.responsible_id;
        if (filter.date_range && filter.date_range.length === 2) {
            formFilters.start_date = filter.date_range[0].toISOString().split("T")[0];
            formFilters.end_date = filter.date_range[1].toISOString().split("T")[0];
        }
        if (filter.state.length) formFilters.states = filter.state;
        if (searchQuery) formFilters.reference = searchQuery;

        commit("dashboard/load", true, { root: true });
        const response = await $http.post(
            '/reports/corrective-actions-download-report', formFilters
        );
        const fileBase64 = response.data.data.file;

        //xlsx
        const link = document.createElement("a");
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${fileBase64}`;

        //name in response.data.filename
        link.download = response.data.data.filename;

        link.click();

        commit("dashboard/load", false, { root: true });
    } catch (error) {
        if (this.$vue.$alertErrorsCallback(error)) return;
        this.$vue.$alertError(error.message);
    } finally {
        commit("dashboard/load", false, { root: true });
    }
}
